import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import {
    defaultQuickSearchSelection,
    sanitizeQuickSearchSelection,
    type QuickSearchMode,
    type QuickSearchSelection,
} from '@/utils/quickSearchFields';

/**
 * The fields each person's quick search looks in, and their Matches all / Matches any
 * choice, per record type (SAU-467). The fields someone ticks for Contacts apply on every
 * list of contacts, in Cmd+K and on the search page.
 *
 * Kept in the browser, as the counsel decided: one key per user and record type,
 * `quick-search-fields:v1:<user id>:<entity id>`. So on a shared office computer one
 * person's choice never reaches the next, and two tabs changing different record types
 * never overwrite each other. A new browser starts from the defaults.
 *
 * It is separate from saved views: opening a view does not change it, and saving a view
 * does not store it.
 *
 * Every read goes back to storage and is cleaned for the record type as it is now, so a
 * field the backend stopped offering is dropped. When storage is refused, a change still
 * holds for the page.
 */

const KEY_PREFIX = 'quick-search-fields:v1';

function storageKey(userId: number | string, entityId: string): string {
    return `${KEY_PREFIX}:${userId}:${entityId}`;
}

function readStored(userId: number | string, entityId: string): unknown {
    try {
        const stored = localStorage.getItem(storageKey(userId, entityId));
        return stored === null ? null : JSON.parse(stored);
    } catch {
        return null;
    }
}

/** Whether the write reached storage. */
function writeStored(userId: number | string, entityId: string, selection: QuickSearchSelection): boolean {
    try {
        localStorage.setItem(storageKey(userId, entityId), JSON.stringify(selection));
        return true;
    } catch {
        return false;
    }
}

export const useQuickSearchSelectionStore = defineStore('quickSearchSelection', () => {
    const authStore = useAuthStore();
    const userId = computed(() => authStore.userData?.id ?? null);

    /** Changes that storage refused, kept for the page. Keyed by user, then record type. */
    const unsaved = ref<Record<string, QuickSearchSelection>>({});
    /** Bumped on every change, here or in another tab, so readers recompute. */
    const revision = ref(0);

    watch(
        userId,
        () => {
            unsaved.value = {};
            revision.value++;
        },
        // Synchronous, so one person's choice is never read under the next person.
        { flush: 'sync' },
    );

    if (typeof window !== 'undefined') {
        window.addEventListener('storage', (event) => {
            if (event.key === null || event.key.startsWith(`${KEY_PREFIX}:`)) {
                revision.value++;
            }
        });
    }

    function selectionFor(entityId: string): QuickSearchSelection {
        // Read so a computed that calls this recomputes on any change.
        void revision.value;
        if (userId.value === null) {
            return defaultQuickSearchSelection(entityId);
        }
        const pending = unsaved.value[`${userId.value}:${entityId}`];
        return sanitizeQuickSearchSelection(entityId, pending ?? readStored(userId.value, entityId));
    }

    function save(entityId: string, next: QuickSearchSelection) {
        if (userId.value === null) {
            return;
        }
        const memoryKey = `${userId.value}:${entityId}`;
        const remaining = { ...unsaved.value };
        delete remaining[memoryKey];
        unsaved.value = writeStored(userId.value, entityId, next) ? remaining : { ...remaining, [memoryKey]: next };
        revision.value++;
    }

    /**
     * Turns one field on or off. The last field cannot be turned off, so a search always
     * looks in at least one field.
     */
    function toggleField(entityId: string, fieldKey: string) {
        const current = selectionFor(entityId);
        const isOn = current.fields.includes(fieldKey);
        if (isOn && current.fields.length === 1) {
            return;
        }
        const fields = isOn ? current.fields.filter((field) => field !== fieldKey) : [...current.fields, fieldKey];
        save(entityId, { ...current, fields });
    }

    function setMode(entityId: string, mode: QuickSearchMode) {
        save(entityId, { ...selectionFor(entityId), mode });
    }

    return { selectionFor, toggleField, setMode, revision };
});
